import { useState } from "react";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Upload, Download, CheckCircle2, AlertTriangle, XCircle, FileSpreadsheet, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function UploadLeadsPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runPreview = async () => {
    if (!file) return toast.error("Select a file first");
    setLoading(true); setResult(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload-leads/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      toast.success(`Parsed ${data.total} rows · ${data.ready_to_import} ready to import`);
    } catch (e) { toast.error(errorMessage(e)); setPreview(null); }
    setLoading(false);
  };

  const confirmImport = async () => {
    if (!preview) return;
    const validRows = preview.rows.filter(r => r.valid);
    if (validRows.length === 0) return toast.error("No valid rows to import. Fix errors and re-upload.");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/upload-leads/confirm", { rows: validRows });
      setResult(data);
      toast.success(`${data.created} leads imported successfully`);
      setPreview(null);
      setFile(null);
    } catch (e) { toast.error(errorMessage(e)); }
    setLoading(false);
  };

  const reset = () => { setPreview(null); setFile(null); setResult(null); };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/upload-template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "leads_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const rowBadge = (r) => {
    if (r.errors.length) return <Badge className="bg-red-500/20 text-red-300 border-red-500/40"><XCircle className="w-3 h-3 mr-1"/>Invalid</Badge>;
    if (r.duplicate_in_db) return <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40"><AlertTriangle className="w-3 h-3 mr-1"/>Duplicate (DB)</Badge>;
    if (r.duplicate_in_file) return <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40"><AlertTriangle className="w-3 h-3 mr-1"/>Duplicate (file)</Badge>;
    return <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40"><CheckCircle2 className="w-3 h-3 mr-1"/>Ready</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="upload-leads-page">
      {/* Upload panel */}
      <div className="card-surface p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="font-heading text-xl text-white flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-amber-500"/> Bulk Upload Leads</div>
            <div className="text-sm text-slate-500 mt-1">
              Upload Excel (.xlsx / .xls) or CSV. Required columns:
              <span className="text-amber-400 font-mono-alt ml-1">name, phone, district, loan_type, amount</span>
            </div>
          </div>
          <Button onClick={downloadTemplate} variant="secondary" data-testid="download-template-btn" className="bg-slate-800 hover:bg-slate-700 text-white rounded-full">
            <Download className="w-4 h-4 mr-2"/>Download Template
          </Button>
        </div>

        {!preview && !result && (
          <div className="border border-dashed border-slate-700 rounded-xl p-8 text-center bg-slate-900/40">
            <Upload className="w-8 h-8 text-slate-500 mx-auto mb-3"/>
            <input data-testid="upload-file-input" type="file" accept=".xlsx,.xls,.csv"
              onChange={e => { setFile(e.target.files[0]); setResult(null); }}
              className="text-sm text-slate-400 file:mr-4 file:px-4 file:py-2 file:rounded-full file:border-0 file:bg-amber-500 file:text-slate-900 file:font-bold"/>
            {file && <div className="text-sm text-amber-400 mt-3">{file.name}</div>}
            <Button disabled={!file || loading} onClick={runPreview} data-testid="upload-preview-btn" className="mt-6 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
              {loading ? "Parsing..." : "Preview File"}
            </Button>
          </div>
        )}

        {result && (
          <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-6" data-testid="upload-result">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle2 className="w-6 h-6 text-emerald-400"/>
              <div className="font-heading text-xl text-white">{result.created} leads imported successfully</div>
            </div>
            <div className="text-sm text-slate-400">Skipped {result.skipped} rows (invalid or duplicate). Round-robin assigned to Telecallers.</div>
            <Button onClick={reset} data-testid="upload-again-btn" className="mt-4 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
              <Upload className="w-4 h-4 mr-2"/>Upload Another File
            </Button>
          </div>
        )}
      </div>

      {/* Preview panel */}
      {preview && (
        <div className="card-surface p-6" data-testid="upload-preview">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="font-heading text-xl text-white">Preview — Review before importing</div>
              <div className="text-sm text-slate-500">Verify the data below. Only rows marked <span className="text-emerald-400 font-bold">Ready</span> will be imported.</div>
            </div>
            <div className="flex gap-2">
              <Button onClick={reset} variant="secondary" data-testid="upload-cancel-btn" className="bg-slate-800 hover:bg-slate-700 text-white rounded-full">
                <RotateCcw className="w-4 h-4 mr-2"/>Cancel
              </Button>
              <Button onClick={confirmImport} disabled={loading || preview.ready_to_import === 0} data-testid="upload-confirm-btn" className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
                {loading ? "Importing..." : `Import ${preview.ready_to_import} Leads`}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="bg-slate-950/60 border border-slate-800 rounded-lg p-3">
              <div className="text-2xl font-display text-white">{preview.total}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Total</div>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3" data-testid="ready-count">
              <div className="text-2xl font-display text-emerald-400">{preview.ready_to_import}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Ready</div>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <div className="text-2xl font-display text-red-400">{preview.invalid}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Invalid</div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <div className="text-2xl font-display text-amber-400">{preview.duplicate_in_db}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Dup (DB)</div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <div className="text-2xl font-display text-amber-400">{preview.duplicate_in_file}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Dup (file)</div>
            </div>
          </div>

          <div className="max-h-[520px] overflow-auto rounded-lg border border-slate-800">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 bg-slate-950/80 sticky top-0 z-10">
                  <TableHead>Row</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>District</TableHead>
                  <TableHead>Loan</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Issues</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.map((r, i) => (
                  <TableRow key={i} className={`border-white/5 ${!r.valid ? "bg-red-500/5" : ""}`} data-testid={`preview-row-${r.row}`}>
                    <TableCell className="font-mono-alt text-slate-500">{r.row}</TableCell>
                    <TableCell>{rowBadge(r)}</TableCell>
                    <TableCell className="text-white">{r.name || <span className="text-slate-600">—</span>}</TableCell>
                    <TableCell className="font-mono-alt">{r.phone || <span className="text-slate-600">—</span>}</TableCell>
                    <TableCell className="text-slate-300 text-sm">
                      {r.district || <span className="text-slate-600">—</span>}
                      {r.district_suggestion && (
                        <div className="text-[10px] text-amber-400 mt-0.5">Did you mean <b>{r.district_suggestion}</b>?</div>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-300 text-sm">{r.loan_type || <span className="text-slate-600">—</span>}</TableCell>
                    <TableCell className="text-slate-300 text-sm">₹{Number(r.amount || 0).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="text-xs text-red-400">
                      {r.errors.length > 0 && r.errors.join(", ")}
                      {r.duplicate_in_db && <div className="text-amber-400">Phone already has an active lead</div>}
                      {r.duplicate_in_file && <div className="text-amber-400">Duplicate phone in file</div>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
